import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Storefront, EyeSlash, Eye, Buildings, ClipboardText, Flag, CheckCircle, ArrowSquareOut, UserCircle } from "@phosphor-icons/react";

function Stat({ label, value, accent }) {
  return (
    <div className="surface rounded-lg p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${accent ? "text-accent" : "text-fg"}`}>{value}</div>
    </div>
  );
}

export default function AdminMarketplace() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pr, rq, rp] = await Promise.all([
        api.get("/admin/marketplace/overview"),
        api.get("/admin/marketplace/profiles"),
        api.get("/admin/marketplace/requests"),
        api.get("/admin/marketplace/reports"),
      ]);
      setOverview(ov.data);
      setProfiles(pr.data.items || []);
      setRequests(rq.data.items || []);
      setReports(rp.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleProfile = async (p) => {
    try {
      const { data } = await api.post(`/admin/marketplace/profiles/${p.consultant_id}/hide`, { unhide: p.admin_hidden });
      setProfiles((x) => x.map((y) => y.consultant_id === p.consultant_id ? { ...y, admin_hidden: data.admin_hidden } : y));
      toast.success(data.admin_hidden ? "Profil masqué" : "Profil réaffiché");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleRequest = async (r) => {
    try {
      const { data } = await api.post(`/admin/marketplace/requests/${r.id}/hide`, { unhide: r.admin_hidden });
      setRequests((x) => x.map((y) => y.id === r.id ? { ...y, admin_hidden: data.admin_hidden } : y));
      toast.success(data.admin_hidden ? "Demande masquée" : "Demande réaffichée");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resolveReport = async (rp) => {
    try {
      await api.post(`/admin/marketplace/reports/${rp.id}/resolve`);
      setReports((x) => x.map((y) => y.id === rp.id ? { ...y, status: "RESOLVED" } : y));
      toast.success("Signalement traité");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-marketplace">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Storefront size={14} className="text-accent" /> Modération Marketplace</div>
          <h1 className="font-display text-3xl font-bold">Talent Marketplace</h1>
        </div>
        <Link to="/admin/organizations" data-testid="admin-mk-orgs-link" className="text-sm text-accent hover:underline flex items-center gap-1 shrink-0">
          Gérer les organisations (approuver / suspendre) <ArrowSquareOut size={14} />
        </Link>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-surface-2 mb-6">
          <TabsTrigger value="overview" data-testid="admin-mk-tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="admin-mk-tab-profiles">Profils ({profiles.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="admin-mk-tab-requests">Demandes ({requests.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-mk-tab-reports">Signalements ({reports.filter((r) => r.status === "OPEN").length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : tab === "overview" && overview ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-mk-overview">
          <Stat label="Profils publiés" value={overview.published_profiles} accent />
          <Stat label="Profils masqués" value={overview.hidden_profiles} />
          <Stat label="ESN approuvées" value={overview.esn_orgs} />
          <Stat label="Clients finaux" value={overview.final_client_orgs} />
          <Stat label="Orgs en attente" value={overview.pending_orgs} />
          <Stat label="Orgs suspendues" value={overview.suspended_orgs} />
          <Stat label="Demandes ouvertes" value={overview.open_requests} />
          <Stat label="Conversations" value={overview.conversations} />
          <Stat label="Signalements ouverts" value={overview.open_reports} accent={overview.open_reports > 0} />
        </div>
      ) : tab === "profiles" ? (
        <div className="space-y-2" data-testid="admin-mk-profiles">
          {profiles.length === 0 ? <p className="text-muted text-sm">Aucun profil publié.</p> : profiles.map((p) => (
            <div key={p.consultant_id} className={`surface rounded-lg p-4 flex items-center justify-between gap-4 ${p.admin_hidden ? "opacity-60" : ""}`} data-testid={`admin-mk-profile-${p.consultant_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0"><UserCircle size={18} weight="duotone" className="text-accent" /></span>
                <div className="min-w-0">
                  <div className="font-semibold text-fg text-sm truncate">{p.display_name} · {p.headline || p.role}</div>
                  <div className="text-xs text-muted truncate flex items-center gap-1"><Buildings size={12} /> {p.org_real_name} ({p.org_type}) · {p.city || "—"} {p.admin_hidden && <span className="text-red-400">· masqué</span>}</div>
                </div>
              </div>
              <button onClick={() => toggleProfile(p)} data-testid={`admin-mk-profile-toggle-${p.consultant_id}`}
                className={`text-xs rounded-md px-3 py-1.5 flex items-center gap-1 shrink-0 border ${p.admin_hidden ? "border-accent/40 text-accent bg-accent/10" : "border-border text-muted hover:text-red-400"}`}>
                {p.admin_hidden ? <><Eye size={14} /> Réafficher</> : <><EyeSlash size={14} /> Masquer</>}
              </button>
            </div>
          ))}
        </div>
      ) : tab === "requests" ? (
        <div className="space-y-2" data-testid="admin-mk-requests">
          {requests.length === 0 ? <p className="text-muted text-sm">Aucune demande.</p> : requests.map((r) => (
            <div key={r.id} className={`surface rounded-lg p-4 flex items-center justify-between gap-4 ${r.admin_hidden ? "opacity-60" : ""}`} data-testid={`admin-mk-request-${r.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-fg text-sm truncate flex items-center gap-1.5"><ClipboardText size={14} className="text-muted" /> {r.title}</div>
                <div className="text-xs text-muted truncate">{r.org_name} ({r.org_type === "FINAL_CLIENT" ? "Client final" : "ESN"}) · {r.status} {r.admin_hidden && <span className="text-red-400">· masquée</span>}</div>
              </div>
              <button onClick={() => toggleRequest(r)} data-testid={`admin-mk-request-toggle-${r.id}`}
                className={`text-xs rounded-md px-3 py-1.5 flex items-center gap-1 shrink-0 border ${r.admin_hidden ? "border-accent/40 text-accent bg-accent/10" : "border-border text-muted hover:text-red-400"}`}>
                {r.admin_hidden ? <><Eye size={14} /> Réafficher</> : <><EyeSlash size={14} /> Masquer</>}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-mk-reports">
          {reports.length === 0 ? <p className="text-muted text-sm">Aucun signalement.</p> : reports.map((rp) => (
            <div key={rp.id} className="surface rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`admin-mk-report-${rp.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-fg text-sm flex items-center gap-1.5"><Flag size={14} className="text-red-400" /> {rp.reporter_org} → {rp.reported_org}</div>
                <div className="text-xs text-muted truncate">{rp.reason || "Sans motif"} · {rp.status}</div>
              </div>
              {rp.status === "OPEN" && (
                <button onClick={() => resolveReport(rp)} data-testid={`admin-mk-report-resolve-${rp.id}`} className="text-xs border border-border rounded-md px-3 py-1.5 flex items-center gap-1 text-muted hover:text-accent shrink-0"><CheckCircle size={14} /> Marquer traité</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
